import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ContactSection() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', company: '', projectType: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', projectType: '', message: '' });
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="relative py-24 bg-gradient-to-b from-[#0a0118] to-[#16213e] overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-5 gap-12">
          <div className="md:col-span-2 space-y-8">
            <div>
              <div className="inline-block px-4 py-2 bg-[#00ff41]/10 border border-[#00ff41] text-[#00ff41] text-xs font-['JetBrains_Mono'] font-semibold mb-6"
                style={{ clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))' }}
              >
                {t('contact.sectionTag')}
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white font-['Space_Grotesk'] mb-4 leading-tight">
                <span className="block text-2xl text-gray-400 mb-2">{t('contact.titleLine1')}</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ff41] to-[#00d9ff]">{t('contact.titleMain')}</span>
              </h2>
              <p className="text-gray-300 leading-relaxed mb-8">
                {t('contact.desc1')}
                <strong className="text-[#00ff41]">{t('contact.highlight1')}</strong>
                <span className="text-gray-300">，</span>
                <strong className="text-[#00d9ff]">{t('contact.highlight2')}</strong>
                {t('contact.highlight3')}
              </p>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-gray-300">
                <div className="w-12 h-12 flex items-center justify-center bg-[#00ff41]/10 border border-[#00ff41]"
                  style={{ clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))' }}
                >
                  <i className="ri-mail-line text-[#00ff41] text-xl"></i>
                </div>
                <div>
                  <div className="text-xs text-gray-500 font-['JetBrains_Mono']">{t('contact.emailLabel')}</div>
                  <div className="font-['JetBrains_Mono']">lilfishegg@gmail.com</div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-gray-300">
                <div className="w-12 h-12 flex items-center justify-center bg-[#00d9ff]/10 border border-[#00d9ff]"
                  style={{ clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))' }}
                >
                  <i className="ri-wechat-line text-[#00d9ff] text-xl"></i>
                </div>
                <div>
                  <div className="text-xs text-gray-500 font-['JetBrains_Mono']">{t('contact.wechatLabel')}</div>
                  <div className="font-['JetBrains_Mono']">PixelMonster_AI</div>
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 font-['JetBrains_Mono'] mb-3">{t('contact.followUs')}</div>
              <div className="flex gap-3">
                {[
                  { icon: 'ri-wechat-fill', color: '#00ff41' },
                  { icon: 'ri-weibo-fill', color: '#00d9ff' },
                  { icon: 'ri-bilibili-fill', color: '#ff006e' },
                  { icon: 'ri-youtube-fill', color: '#9d4edd' }
                ].map((social, idx) => (
                  <button key={idx}
                    className="w-12 h-12 flex items-center justify-center bg-[#1a0b2e]/50 border border-gray-700 hover:scale-110 transition-all duration-300 cursor-pointer"
                    style={{ clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = social.color; (e.currentTarget.querySelector('i') as HTMLElement).style.color = social.color; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#374151'; (e.currentTarget.querySelector('i') as HTMLElement).style.color = '#9ca3af'; }}
                  >
                    <i className={`${social.icon} text-xl text-gray-400 transition-colors duration-300`}></i>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-['JetBrains_Mono'] text-gray-400 mb-2">{t('contact.nameLabel')}</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required
                    className="w-full px-4 py-3 bg-[#1a0b2e]/50 border-2 border-gray-700 text-white font-['Space_Grotesk'] focus:border-[#00ff41] focus:outline-none transition-colors duration-300 text-sm"
                    style={{ clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))' }}
                    placeholder={t('contact.namePlaceholder')} />
                </div>
                <div>
                  <label className="block text-sm font-['JetBrains_Mono'] text-gray-400 mb-2">{t('contact.emailFieldLabel')}</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required
                    className="w-full px-4 py-3 bg-[#1a0b2e]/50 border-2 border-gray-700 text-white font-['Space_Grotesk'] focus:border-[#00ff41] focus:outline-none transition-colors duration-300 text-sm"
                    style={{ clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))' }}
                    placeholder={t('contact.emailPlaceholder')} />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-['JetBrains_Mono'] text-gray-400 mb-2">{t('contact.companyLabel')}</label>
                  <input type="text" name="company" value={formData.company} onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#1a0b2e]/50 border-2 border-gray-700 text-white font-['Space_Grotesk'] focus:border-[#00ff41] focus:outline-none transition-colors duration-300 text-sm"
                    style={{ clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))' }}
                    placeholder={t('contact.companyPlaceholder')} />
                </div>
                <div>
                  <label className="block text-sm font-['JetBrains_Mono'] text-gray-400 mb-2">{t('contact.projectTypeLabel')}</label>
                  <select name="projectType" value={formData.projectType} onChange={handleChange} required
                    className="w-full px-4 py-3 bg-[#1a0b2e]/50 border-2 border-gray-700 text-white font-['Space_Grotesk'] focus:border-[#00ff41] focus:outline-none transition-colors duration-300 cursor-pointer text-sm"
                    style={{ clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))' }}
                  >
                    <option value="">{t('contact.projectTypePlaceholder')}</option>
                    <option value="tvc">{t('contact.type1')}</option>
                    <option value="mv">{t('contact.type2')}</option>
                    <option value="digital">{t('contact.type3')}</option>
                    <option value="exhibition">{t('contact.type4')}</option>
                    <option value="other">{t('contact.type5')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-['JetBrains_Mono'] text-gray-400 mb-2">{t('contact.messageLabel')}</label>
                <textarea name="message" value={formData.message} onChange={handleChange} required rows={6} maxLength={500}
                  className="w-full px-4 py-3 bg-[#1a0b2e]/50 border-2 border-gray-700 text-white font-['Space_Grotesk'] focus:border-[#00ff41] focus:outline-none transition-colors duration-300 resize-none text-sm"
                  style={{ clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))' }}
                  placeholder={t('contact.messagePlaceholder')}
                ></textarea>
                <div className="text-xs text-gray-500 font-['JetBrains_Mono'] mt-1 text-right">{formData.message.length}/500</div>
              </div>
              <button type="submit" disabled={isSubmitting}
                className="w-full px-8 py-4 bg-[#00ff41] text-black font-['JetBrains_Mono'] text-sm font-bold hover:bg-[#00d9ff] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                style={{ clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}
              >
                {isSubmitting ? (<><i className="ri-loader-4-line animate-spin text-lg"></i><span>{t('contact.submitting')}</span></>) :
                  submitStatus === 'success' ? (<><i className="ri-check-line text-lg"></i><span>{t('contact.success')}</span></>) :
                  (<span>{t('contact.submitBtn')}</span>)}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
